import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import {
  Button,
  EditIcon,
  Pagination,
  Select,
  TableColumns,
  TableColumnsColumnProps,
  Text,
} from '@mercai/clever'

import { DEFAULT_LIMIT_PAGE } from '@/constants'
import { formatCurrency, formatNumber, useParamsState } from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import { useForecastList } from '@/services'
import { SupplierForecastProps, SupplierProductProps } from '@/types'

// import { SupplierProductDrawerForm } from '../supplier-product-drawer-form'

import { SupplierProductTableProps } from './supplier-product-table.types'

import {
  SupplierForecastDrawerForm,
  SupplierProductCostExpirationTag,
} from '@/components'
import styles from './supplier-product-table.module.css'

export const SupplierProductTable = ({
  uuid,
}: SupplierProductTableProps) => {
  const { t } = useTranslation('screenCatalogProduct')

  const [filter, setFilter] = useParamsState({
    page: '1',
    limit: `${DEFAULT_LIMIT_PAGE}`,
    supplierProduct_CatalogProduct_Uuid: uuid || '',
  })

  const [totalPages, setTotalPages] = useState(1)

  const handleFilter = (changedKey: string, newValue: string) => {
    setFilter({ ...filter, [changedKey]: newValue })
  }

  const { data, isLoading } = useForecastList({
    params: {
      page: filter.page,
      limit: filter.limit,
      supplierProduct_CatalogProduct_Uuid: uuid || '',
    },
  })

  const handleEditSupplierProduct = (record: SupplierForecastProps) => {
    setFilter({
      ...filter,
      updateSupplierForecastForm: record?.uuid || '',
      updateSupplierForecastCatalogProductForm:
        record?.supplierProduct?.catalogProduct?.uuid || '',
    })
  }

  const columns: TableColumnsColumnProps<SupplierForecastProps>[] = [
    {
      title: t('detailSupplierProductsTableName'),
      key: 'name',
      render: ({ record }) => (
        <Link
          to={`/${ROUTES_ADMIN.SUPPLY.SUPPLIERS.path}/${record?.supplierProduct?.supplier?.uuid || record?.supplierProduct?.supplierWarehouse?.supplier?.uuid}`}
          className={styles['supplier-name-link']}
        >
          <Text className={styles['supplier-name']}>
            {record?.supplierProduct?.supplier?.name ||
              record?.supplierProduct?.supplierWarehouse?.supplier?.name}
          </Text>
          <Text className={styles['supplier-email']}>
            {record?.supplierProduct?.supplier?.email ||
              record?.supplierProduct?.supplierWarehouse?.supplier?.email}
          </Text>
        </Link>
      ),
    },
    {
      title: t('detailSupplierProductsTableSKU'),
      key: 'reference',
      width: '8rem',
      render: ({ record }) => record?.supplierProduct?.reference || '-',
    },
    {
      title: t('detailSupplierProductsTablePriceWithoutIVA'),
      key: 'priceWithoutIVA',
      width: '9rem',
      render: ({ record }) =>
        record?.cost
          ? formatCurrency(record.cost, 'es-MX', record.currency || 'USD', 3)
          : '-',
    },
    {
      title: t('detailSupplierProductsTableStatus'),
      key: 'costExpirationDate',
      width: '7rem',
      render: ({ record }) => (
        <SupplierProductCostExpirationTag
          costExpirationDate={record?.expiresAt}
        />
      ),
    },
    {
      title: t('detailSupplierProductsTableMin'),
      key: 'minimumPurchaseQuantity',
      width: '10rem',
      render: ({ record }) =>
        record?.supplierProduct?.minimumPurchaseQuantity
          ? `${formatNumber(record?.supplierProduct?.minimumPurchaseQuantity)} Kg`
          : '-',
    },
    {
      title: t('detailSupplierProductsTableAvailable'),
      key: 'quantity',
      width: '12rem',
      render: ({ record }) =>
        record?.quantity ? `${formatNumber(record?.quantity)} Kg` : '-',
    },
    {
      title: t('detailSupplierProductsTableLocation'),
      key: 'supplierWarehouseUuid',
      width: '12rem',
      render: ({ record }) => (
        <div className={styles['warehouse-column']}>
          <img
            src={
              record?.supplierProduct?.supplierWarehouse?.country === 'US'
                ? '/images/flag-us.svg'
                : '/images/flag-mx.svg'
            }
            alt={'flag'}
          />
          {record?.supplierProduct?.supplierWarehouse?.city}
        </div>
      ),
    },
    {
      title: t(''),
      key: 'actions',
      width: '5rem',
      render: ({ record }) => (
        <Button
          variant="secondary"
          model="round"
          onClick={() => handleEditSupplierProduct(record)}
        >
          <EditIcon />
        </Button>
      ),
    },
  ]

  const ExpandedComponent = ({ record }: { record: SupplierProductProps }) => {
    return (
      <div className={styles['expanded-content']}>
        <div>
          <Text>
            <strong>
              {t('detailSupplierProductsTableAgreementReference')}
            </strong>
            {': '}
            {record?.forecasts?.[0]?.agreementReference || '-'}
          </Text>
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (data?.data?.count) {
      setTotalPages(Math.ceil(data?.data?.count / +filter.limit))
    }
  }, [data])

  return (
    <div className={styles.core}>
      <TableColumns<SupplierForecastProps>
        isLoading={isLoading}
        columns={columns}
        data={data?.data?.results || []}
        expandableComponent={ExpandedComponent}
      />
      <div className={styles.pagination}>
        <div className={styles['pagination-show-registers']}>
          <Text>
            {t('detailSupplierPaginationShowRegisters', {
              showNumber:
                (data?.data?.count || 0) < +filter.limit
                  ? data?.data?.count
                  : +filter.limit,
              totalNumber: data?.data?.count || 0,
            })}
          </Text>

          <Select
            value={filter.limit}
            onChange={(value) => handleFilter('limit', value)}
            options={[
              { label: '10', value: '10' },
              { label: '20', value: '20' },
              { label: '50', value: '50' },
              { label: '100', value: '100' },
            ]}
          />
        </div>

        <Pagination
          totalPages={totalPages}
          onChange={(page) => handleFilter('page', `${page}`)}
          currentPage={+filter.page as number}
        />
      </div>

      <SupplierForecastDrawerForm
        isOpen={!!filter.updateSupplierForecastForm}
        onHandleOpen={() =>
          setFilter({
            ...filter,
            updateSupplierForecastForm: '',
            updateSupplierForecastCatalogProductForm: '',
          })
        }
        catalogProductUuid={uuid}
        supplierForecastUuid={filter.updateSupplierForecastForm}
      />
    </div>
  )
}
